/**
 * Approval Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as approvalService from '../api/services/approval.service';
import { toast } from '../stores/toast.store';

const KEYS = {
  stats: ['approval', 'stats'] as const,
  pending: ['approval', 'pending'] as const,
  history: ['approval', 'history'] as const,
  detail: (id: number) => ['approval', 'detail', id] as const,
  employees: ['approval', 'employees'] as const,
  allLeave: ['approval', 'allLeave'] as const,
  allOvertime: ['approval', 'allOvertime'] as const,
  allAttendance: ['approval', 'allAttendance'] as const,
  paymentDelays: ['approval', 'paymentDelays'] as const,
};

/**
 * Hook for fetching approval stats
 */
export function useApprovalStats() {
  return useQuery({
    queryKey: KEYS.stats,
    queryFn: async () => {
      const result = await approvalService.getApprovalStats();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch stats');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching pending approvals
 */
export function usePendingApprovals() {
  return useQuery({
    queryKey: KEYS.pending,
    queryFn: async () => {
      const result = await approvalService.getPendingApprovals();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch pending');
      }
      return result.data!;
    },
  });
}

interface UseApprovalHistoryParams {
  offset?: number;
  limit?: number;
}

/**
 * Hook for fetching approval history
 */
export function useApprovalHistory(params: UseApprovalHistoryParams = {}) {
  return useQuery({
    queryKey: [...KEYS.history, params],
    queryFn: async () => {
      const result = await approvalService.getApprovalHistory(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch history');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching approval detail
 */
export function useApprovalDetail(id: number | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const result = await approvalService.getApprovalDetail(id!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch detail');
      }
      return result.data!;
    },
    enabled: !!id,
  });
}

/**
 * Hook for approving a review
 */
export function useApproveReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, comment }: { id: number; comment?: string }) => {
      const result = await approvalService.approveReview(id, comment);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to approve');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.pending });
      queryClient.invalidateQueries({ queryKey: KEYS.history });
      queryClient.invalidateQueries({ queryKey: KEYS.stats });
      toast.success('已核准');
    },
    onError: (error: Error) => {
      toast.error('核准失敗', error.message);
    },
  });
}

/**
 * Hook for rejecting a review
 */
export function useRejectReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, comment }: { id: number; comment?: string }) => {
      const result = await approvalService.rejectReview(id, comment);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to reject');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.pending });
      queryClient.invalidateQueries({ queryKey: KEYS.history });
      queryClient.invalidateQueries({ queryKey: KEYS.stats });
      toast.success('已駁回');
    },
    onError: (error: Error) => {
      toast.error('駁回失敗', error.message);
    },
  });
}

/**
 * Hook for fetching employees (manager only)
 */
export function useManagerEmployees() {
  return useQuery({
    queryKey: KEYS.employees,
    queryFn: async () => {
      const result = await approvalService.getManagerEmployees();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch employees');
      }
      return result.data!;
    },
  });
}

interface ManagerQueryParams {
  offset?: number;
  limit?: number;
  state?: string;
  employee_id?: number;
  date_from?: string;
  date_to?: string;
}

/**
 * Hook for all leave records (manager only)
 */
export function useAllLeaveRecords(params: ManagerQueryParams = {}) {
  return useQuery({
    queryKey: [...KEYS.allLeave, params],
    queryFn: async () => {
      const result = await approvalService.getAllLeaveRecords(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for all overtime records (manager only)
 */
export function useAllOvertimeRecords(params: ManagerQueryParams = {}) {
  return useQuery({
    queryKey: [...KEYS.allOvertime, params],
    queryFn: async () => {
      const result = await approvalService.getAllOvertimeRecords(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for all attendance records (manager only)
 */
export function useAllAttendanceRecords(params: ManagerQueryParams = {}) {
  return useQuery({
    queryKey: [...KEYS.allAttendance, params],
    queryFn: async () => {
      const result = await approvalService.getAllAttendanceRecords(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for pending payment delay approvals (manager only)
 */
export function usePaymentDelays(params: { offset?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: [...KEYS.paymentDelays, params],
    queryFn: async () => {
      const result = await approvalService.getPaymentDelays(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch payment delays');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for reviewing a payment delay (manager only)
 */
export function useReviewPaymentDelay() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      action,
      note,
    }: {
      id: number;
      action: 'approve' | 'reject';
      note?: string;
    }) => {
      const result = await approvalService.reviewPaymentDelay(id, action, note);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to review');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.paymentDelays });
      queryClient.invalidateQueries({ queryKey: KEYS.stats });
      toast.success('審核完成');
    },
    onError: (error: Error) => {
      toast.error('審核失敗', error.message);
    },
  });
}
